import { useNavigate } from "react-router-dom";
import { Alert, Button, Space, Text, useToast } from "@app/ui";
import { useLang } from "../i18n.js";
import { resolveApiError, type ResolvedError } from "./resolve.js";

interface ErrorAlertProps {
  /** A raw thrown error, or an already-resolved one. */
  error: unknown;
  /** Called when the user hits a "retry" action. */
  onRetry?: () => void;
  className?: string;
}

function isResolved(e: unknown): e is ResolvedError {
  return typeof e === "object" && e !== null && "title" in e && "fieldErrors" in e;
}

/**
 * Renders a localized, directing error: title + hint + an action button that
 * moves the user toward a fix (sign in again, go to billing, retry, reload) and,
 * when available, a copyable support reference.
 */
export function ErrorAlert({ error, onRetry, className }: ErrorAlertProps) {
  const { lang } = useLang();
  const navigate = useNavigate();
  const toast = useToast();
  const resolved = isResolved(error) ? error : resolveApiError(error, lang);

  const runAction = () => {
    const action = resolved.action;
    if (!action) return;
    switch (action.kind) {
      case "retry":
        onRetry?.();
        break;
      case "reload":
        window.location.reload();
        break;
      case "login":
        navigate("/login");
        break;
      case "route":
        navigate(action.to);
        break;
      case "support":
        break;
    }
  };

  const copySupport = () => {
    if (!resolved.correlationId) return;
    void navigator.clipboard?.writeText(resolved.correlationId);
    toast.success(lang === "tr" ? "Destek referansı kopyalandı" : "Support reference copied");
  };

  // A "retry" action is only meaningful when a handler was supplied.
  const showAction = resolved.action && !(resolved.action.kind === "retry" && !onRetry);

  return (
    <Alert type="error" className={className}>
      <Space direction="vertical" size="sm" style={{ width: "100%" }}>
        <Text style={{ fontWeight: 600 }}>{resolved.title}</Text>
        {resolved.hint && (
          <Text small style={{ opacity: 0.9 }}>
            {resolved.hint}
          </Text>
        )}
        {(showAction || resolved.correlationId) && (
          <Space size="sm" wrap>
            {showAction && resolved.action && (
              <Button size="sm" variant="default" onClick={runAction}>
                {resolved.action.label}
              </Button>
            )}
            {resolved.correlationId && (
              <Button size="sm" variant="subtle" onClick={copySupport}>
                {lang === "tr" ? "Destek ref: " : "Support ref: "}
                {resolved.correlationId}
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Alert>
  );
}
